import { Divider, Group, LoadingOverlay, Radio, Select, SelectProps, Stack } from '@mantine/core';
import { Fragment, useMemo, useRef, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AgentFinancialResponse, AgentSource } from '../../../../api/events-client/events-client.type';
import { ContentPlaceholder } from '../../../../components/content-placeholder/content-placeholder';
import { formatCurrency, getCurrency } from '../../../../helpers/currency-helpers';
import { formatMonthDate } from '../../../../helpers/date-time-helpers/formatting/formatting';
import { exportChart } from '../../../../helpers/image-downloader/image-downloader';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import {
  setAgentCostsAgentSource,
  setAgentCostsChartType,
  setAgentCostsViewBy,
} from '../../../../store/financials-store/financials-store.actions';
import { ChartType } from '../../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { baseBlack, inkBase, secondaryBase, secondaryLight } from '../../../../styles/design-tokens';
import { Text } from '../../../../ui-library/typography/typography';
import { AGENT_SOURCES_ICONS, AGENT_SOURCES_LABELS } from '../../../agentic-process/agentic-process.type';
import { formatTokenCount } from '../../../flora/components/context-usage-indicator/context-usage-indicator.helpers';
import { DEFAULT_CHART_COLORS } from '../../constants';
import { LineChartDot } from '../../../process-analysis/components/line-chart/line-chart-dot';
import { StyledButton } from '../shared/chart-display-type-button-set';
import { CustomTooltip } from '../shared/custom-tooltip';
import { Legend as AgentCostsLegend } from '../shared/Legend';
import { assignColorsToItems, getItemColor } from '../shared/shared.helpers';
import { maybeGetNullLabelForSeries } from '../allocation-of-costs/allocation-of-costs.helpers';
import { addTrendLines } from '../allocation-of-costs/allocation-of-costs.helpers';
import { getAgentCostsChartData } from './agent-costs.helpers';

type ChartProps = {
  data: AgentFinancialResponse | undefined;
  isPending: boolean;
};

function Chart({ data, isPending }: ChartProps) {
  const { selectedChartType, viewBy, selectedCategory, selectedOptions, selectedTrendLines, selectedAgentSource } =
    useFinancialsStore((state) => state.filters.agentCosts);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);

  const chartRef = useRef<HTMLDivElement>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);

  const agentSourceOptions = useMemo(() => {
    if (!data?.agent_sources) {
      return [{ value: 'all', label: 'All Sources' }];
    }
    return [
      { value: 'all', label: 'All Sources' },
      ...data.agent_sources.map((source) => ({
        value: source as string,
        label: AGENT_SOURCES_LABELS[source] || source,
      })),
    ];
  }, [data]);

  const renderSelectOption: SelectProps['renderOption'] = ({ option }) => (
    <Group flex="1" gap="xs">
      {option.value !== 'all' && <img src={AGENT_SOURCES_ICONS[option.value as AgentSource]} height={12} />}
      <Text size="small">{option.label}</Text>
    </Group>
  );

  const chartData = useMemo(() => {
    if (!data) return [];
    const rawData = getAgentCostsChartData(data, selectedCategory, selectedAgentSource as AgentSource | 'all', viewBy);
    return addTrendLines(rawData);
  }, [data, selectedCategory, selectedAgentSource, viewBy]);

  const colorAssignments = assignColorsToItems(selectedOptions || [], DEFAULT_CHART_COLORS);

  const chart = useMemo(() => {
    // Guard against stale hoveredItem: if `selectedOptions` changes and the previously
    // hovered series is no longer present, every series would be treated as inactive
    // and dimmed. Validate hoveredItem against the current option set so that a stale
    // hover behaves like "no hover" (all series rendered active).
    const hasValidHoveredItem = hoveredItem !== null && (selectedOptions || []).includes(hoveredItem);

    const sharedChartComponents = (
      <>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis dataKey="date" tickFormatter={(value) => formatMonthDate(value)} tick={{ fontSize: 12 }} />
        <YAxis
          yAxisId="cost"
          orientation="left"
          tick={{ fontSize: 12 }}
          tickFormatter={
            viewBy === 'cost'
              ? (value) => formatCurrency(value, { currency: currencyCode })
              : (value) => formatTokenCount(value)
          }
          label={viewBy === 'cost' ? { value: currencyCode, position: 'insideLeft', offset: 0, angle: -90 } : undefined}
        />
        <Tooltip
          content={
            <CustomTooltip
              filterFn={(item) => (selectedOptions || []).includes(item.name || '')}
              nameFormatter={(item) => maybeGetNullLabelForSeries(item.name!)}
              valueFormatter={(item) =>
                viewBy === 'cost'
                  ? formatCurrency(Number(item.value), { currency: currencyCode })
                  : formatTokenCount(Number(item.value))
              }
              getStrokeColor={(item) => getItemColor(item.name!, colorAssignments)}
            />
          }
        />
      </>
    );

    switch (selectedChartType) {
      case ChartType.Line:
        return (
          <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: 160 }}>
            <ResponsiveContainer>
              <LineChart data={chartData}>
                {sharedChartComponents}
                {(selectedOptions || []).map((option) => {
                  const color = getItemColor(option, colorAssignments);
                  const isActive = !hasValidHoveredItem || hoveredItem === option;
                  return (
                    <Fragment key={option}>
                      <Line
                        yAxisId="cost"
                        type="monotone"
                        dataKey={option}
                        stroke={color}
                        strokeWidth={isActive ? 2 : 1}
                        opacity={isActive ? 1 : 0.3}
                        fill={color}
                        dot={<LineChartDot color={color} />}
                      />
                      <Line
                        yAxisId="cost"
                        type="monotone"
                        dataKey={`${option}_trend`}
                        stroke={color}
                        strokeWidth={isActive ? 2 : 1}
                        strokeDasharray="5 5"
                        opacity={isActive ? 1 : 0.3}
                        dot={false}
                        activeDot={false}
                        hide={!(selectedTrendLines || []).includes(option)}
                      />
                    </Fragment>
                  );
                })}
                <Legend content={<AgentCostsLegend hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} />} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        );
      case ChartType.Bar:
        return (
          <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: 160 }}>
            <ResponsiveContainer>
              <ComposedChart data={chartData}>
                {sharedChartComponents}
                {(selectedOptions || []).map((option) => {
                  const color = getItemColor(option, colorAssignments);
                  const isActive = !hasValidHoveredItem || hoveredItem === option;
                  return (
                    <Fragment key={option}>
                      <Bar yAxisId="cost" dataKey={option} fill={color} opacity={isActive ? 1 : 0.3} />
                      <Line
                        yAxisId="cost"
                        type="monotone"
                        dataKey={`${option}_trend`}
                        stroke={color}
                        strokeWidth={isActive ? 2 : 1}
                        strokeDasharray="5 5"
                        opacity={isActive ? 1 : 0.3}
                        dot={false}
                        activeDot={false}
                        hide={!(selectedTrendLines || []).includes(option)}
                      />
                    </Fragment>
                  );
                })}
                <Legend content={<AgentCostsLegend hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} />} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        );
      default:
        return null;
    }
  }, [
    chartData,
    selectedChartType,
    selectedOptions,
    selectedTrendLines,
    colorAssignments,
    viewBy,
    currencyCode,
    hoveredItem,
  ]);

  return (
    <Stack h="100%" style={{ overflow: 'hidden', position: 'relative' }}>
      <LoadingOverlay visible={isPending} loaderProps={{ color: secondaryBase }} />
      {!isPending && chartData.length === 0 ? (
        <ContentPlaceholder icon="event_busy" text="No filtered data" />
      ) : (
        <Fragment>
          <Group mb={12} justify="space-between">
            <Group>
              <StyledButton
                label="Line Chart"
                iconName="stacked_line_chart"
                onClick={() => setAgentCostsChartType(ChartType.Line)}
                selected={selectedChartType === ChartType.Line}
              />
              <StyledButton
                label="Bar Chart"
                iconName="bar_chart"
                onClick={() => setAgentCostsChartType(ChartType.Bar)}
                selected={selectedChartType === ChartType.Bar}
              />
            </Group>
            <Group>
              <Group gap="xs">
                <Text size="small">Agent Source</Text>
                <Select
                  value={selectedAgentSource}
                  data={agentSourceOptions}
                  onChange={(value) => setAgentCostsAgentSource(value as string)}
                  style={{ width: '150px' }}
                  radius="xl"
                  renderOption={renderSelectOption}
                  allowDeselect={false}
                />
              </Group>
              <Divider orientation="vertical" />
              <Group>
                <Radio.Group
                  onChange={(value) => setAgentCostsViewBy(value as 'cost' | 'tokens')}
                  value={viewBy}
                  styles={{ label: { fontWeight: 600, marginLeft: '30px' } }}
                >
                  <Group>
                    <Radio
                      value="cost"
                      label="Cost"
                      variant="outline"
                      color={secondaryLight}
                      styles={{
                        radio: { cursor: 'pointer' },
                        label: { fontWeight: 550, color: viewBy === 'cost' ? baseBlack : inkBase },
                      }}
                    />
                    <Radio
                      value="tokens"
                      label="Tokens"
                      variant="outline"
                      color={secondaryLight}
                      styles={{
                        radio: { cursor: 'pointer' },
                        label: { fontWeight: 550, color: viewBy === 'tokens' ? baseBlack : inkBase },
                      }}
                    />
                  </Group>
                </Radio.Group>
                <StyledButton
                  label="Export"
                  iconName="download"
                  onClick={() => chartRef.current && exportChart(chartRef.current, 'agent-costs-chart.png')}
                  selected={true}
                />
              </Group>
            </Group>
          </Group>

          {chart}
        </Fragment>
      )}
    </Stack>
  );
}

export { Chart };
